/**
 * A patient's recorded sex, read the way the server reads it.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * `usr.Member.Gender` is free text and holds more than one spelling of each sex: "Female"
 * and "F", "Male" and "M" (feeds and imports write the letters). The server's reader,
 * `OO.Domain/MemberInfo/MemberSex.cs` (`IsFemale`), matches the trimmed INITIAL, and the
 * patient pickers' Female / Male filter runs through that same reading in SQL
 * (`MemberSex.IsRecordedAs`). This is its twin for the screen — so a list filtered to
 * "Female" can never print a row labelled anything else.
 *
 * Unknown stays unknown: a blank value reads as null, never as Male, and a value that
 * starts with neither letter is printed exactly as recorded rather than guessed at.
 *
 * ⚠ Do not hand-roll `g === 'F' || g === 'Female'` beside a screen. That is how the same
 * column came to be read four different ways; `patient-sex.ratchet.test.ts` lists the
 * copies that predate this file and fails on a new one.
 */

export type RecordedSex = 'F' | 'M';

/** 'F', 'M', or null when the record does not say. Mirrors `MemberSex.IsFemale` on the server. */
export const recordedSex = (gender?: string | null): RecordedSex | null => {
  const g = (gender ?? '').trim().toUpperCase();
  if (g.startsWith('F')) return 'F';
  if (g.startsWith('M')) return 'M';
  return null;
};

/**
 * The word to print: "Female" / "Male" (translated through `t` when given), the value exactly
 * as stored when it is neither, or null when nothing was recorded.
 */
export const sexLabel = (gender?: string | null, t?: (key: string) => string): string | null => {
  const sex = recordedSex(gender);
  if (sex === 'F') return t ? t('female') : 'Female';
  if (sex === 'M') return t ? t('male') : 'Male';
  const recorded = (gender ?? '').trim();
  return recorded || null;
};
